// Inspections — list via POST /inspections/all (server-side pagination +
// Available/Archived split on `status`). Create/save-draft/update map to the
// inspections CRUD endpoints; the four lifecycle verbs and DELETE are both
// bulk-shaped (POST /inspections/bulk-action, POST /inspections/bulk-delete) —
// there is no single-delete route. Response normalization stays isolated in
// toInspection so any backend drift is a one-file fix (same idiom as
// datasourceApi / projectsApi).

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

// Backend enums (confirmed).
export type InspectionStatus = 'active' | 'archived';
export type InspectionState = 'active' | 'draft' | 'suspended';
export type InspectionPriority = 'low' | 'medium' | 'high';

/** Backend `orderBy.key` values — the confirmed set. The sortable list columns
 *  are keyed by these members, so the page's sort state goes on the wire as-is. */
export enum InspectionSortKey {
  TITLE = 'title',
  REFERENCE_NUMBER = 'referenceNumber',
  PRIORITY = 'priority',
  STATE = 'state',
  START_DATE = 'startDate',
}

/** Due-date filter mode on the list body — sent only when the user picks one. */
export enum InspectionDueDateFilter {
  ANY_DATE = 'any',
  NO_DATE = 'none',
  CUSTOM = 'custom',
}

export interface InspectionRef {
  id: string;
  title: string;
  /** kebab-case lucide name. */
  icon: string;
}

/** A person shown in the list (created-by cell) — id drives the avatar tone. */
export interface InspectionPerson {
  id: string;
  firstName: string;
  lastName: string;
}

/** Populated company-structure refs on rows — drive the structure cell and the
 *  edit drawer's locked-select fallbacks. */
export interface InspectionLevelRef {
  id: string;
  name: string;
  level: number;
  icon: string; // kebab-case lucide name
}
export interface InspectionNodeRef {
  id: string;
  name: string;
}

export interface InspectionListItem {
  id: string;
  referenceNumber: number;
  title: string;
  description: string;
  priority: InspectionPriority | '';
  status: InspectionStatus;
  state: InspectionState;
  form: InspectionRef | null;
  project: InspectionRef | null;
  startDate: string;
  endDate: string;
  tags: { id: string; title: string }[];
  companyLevel: InspectionLevelRef | null;
  companyNode: InspectionNodeRef | null;
  createdBy: InspectionPerson | null;
  createdAt: string;
}

export interface InspectionsPage {
  items: InspectionListItem[];
  total: number;
}

/** POST /inspections/all body — status/page/limit required; the rest are the
 *  filter params (all arrays, matched with `$in` on the backend). */
export interface InspectionsQueryArgs {
  status: InspectionStatus;
  page: number;
  limit: number;
  search?: string;
  priority?: InspectionPriority[];
  state?: InspectionState[];
  tags?: string[];
  form?: string[];
  project?: string[];
  companyNode?: string[];
  createdBy?: string[];
  assignedTo?: string[];
  inspectionDueDate?: InspectionDueDateFilter;
  /** yyyy-mm-dd; sent only with CUSTOM. */
  inspectionDueDateStart?: string;
  inspectionDueDateEnd?: string;
  orderBy?: { key: InspectionSortKey; type: 'asc' | 'desc' };
}

/** GET /inspections/accessible-inspection-users item (confirmed: already id/name). */
export interface InspectionUserOption {
  id: string;
  name: string;
}

/** The drawer's full field bag — what create and save-draft accept. */
export interface InspectionFields {
  title: string;
  description?: string;
  form?: string;
  project?: string;
  priority?: InspectionPriority;
  tags?: string[];
  startDate?: string;
  endDate?: string;
  companyLevel?: string;
  companyNode?: string[]; // sent as [id]
  location?: string;
  coordinates?: string;
  /** Auto-fill submissions — only meaningful with a level + node picked. */
  autoFill?: boolean;
}

/** POST /inspections/create body — title + form are required by the backend. */
export interface CreateInspectionPayload extends InspectionFields {
  form: string;
}

/** POST /inspections/save-draft body — only the title is required. With
 *  `inspectionId` it updates an existing draft instead of creating one. */
export interface SaveDraftInspectionPayload extends InspectionFields {
  inspectionId?: string;
}

/** PATCH /inspections/update/:id body — the edit drawer's single save path.
 *  The four structural fields (form/project/companyLevel/companyNode) ride along
 *  only while the inspection is a draft (they stay editable there); once it
 *  leaves the draft state the drawer locks them and they are omitted.
 *  `autoFill` mirrors the drawer's toggle. */
export interface UpdateInspectionPayload {
  title: string;
  description?: string;
  priority?: InspectionPriority;
  tags?: string[];
  startDate?: string;
  endDate?: string;
  location?: string;
  coordinates?: string;
  autoFill: boolean;
  // Draft-only — sent while `state === 'draft'`, omitted otherwise.
  form?: string;
  project?: string;
  companyLevel?: string;
  companyNode?: string[]; // sent as [id], same as InspectionFields
}

// ---- raw shapes (per the confirmed response; unseen fields stay defensive) ----
type ApiRef = { _id: string; title: string; icon: string } | null;
interface ApiInspectionUser {
  // The populated company-user: its own id plus the linked user's names.
  _id: string;
  user: { firstName: string; lastName: string };
}
interface ApiInspection {
  _id: string;
  referenceNumber: number;
  title?: string | null;
  description?: string | null;
  priority?: InspectionPriority | null;
  status?: InspectionStatus | null;
  state?: InspectionState | null;
  form?: ApiRef;
  project?: ApiRef;
  startDate?: string | null;
  endDate?: string | null;
  tags?: { _id: string; title: string }[];
  // Populated on both the list and the detail (confirmed responses).
  companyLevel?: { _id: string; icon: string; level: number; name: string };
  companyNode?: Array<{ _id: string; name: string }>;
  autoFill?: boolean;
  createdBy?: ApiInspectionUser | null;
  createdAt?: string | null;
}
type ApiInspectionsPage =
  | { items?: ApiInspection[]; totalRecords?: number; total?: number }
  | ApiInspection[];

// Normalize the populated company-user into the list person; null when there is
// no name to show (e.g. a deleted user), so the cell renders a dash.
const toPerson = (raw?: ApiInspectionUser | null): InspectionPerson | null => {
  if (!raw) return null;
  const { firstName, lastName } = raw.user;
  if (!firstName && !lastName) return null;
  return { id: raw._id, firstName, lastName };
};

const toInspection = (i: ApiInspection): InspectionListItem => ({
  id: i._id,
  referenceNumber: i.referenceNumber,
  title: i.title ?? '',
  description: i.description ?? '',
  priority: i.priority ?? '',
  status: i.status ?? 'active',
  state: i.state ?? 'active',
  form: i.form ? { id: i.form._id, title: i.form.title, icon: i.form.icon } : null,
  project: i.project ? { id: i.project._id, title: i.project.title, icon: i.project.icon } : null,
  startDate: i.startDate ?? '',
  endDate: i.endDate ?? '',
  tags: (i.tags ?? []).map((t) => ({ id: t._id, title: t.title })),
  companyLevel: i.companyLevel
    ? {
        id: i.companyLevel._id,
        name: i.companyLevel.name,
        level: i.companyLevel.level,
        icon: i.companyLevel.icon,
      }
    : null,
  companyNode: i.companyNode?.[0]
    ? { id: i.companyNode[0]._id, name: i.companyNode[0].name }
    : null,
  createdBy: toPerson(i.createdBy),
  createdAt: i.createdAt ?? '',
});

/** GET /inspections/details/:id — the list item (level/node arrive populated
 *  there too) plus the drawer-only fields. formVersion is mapped but not
 *  rendered anywhere yet. */
export interface InspectionDetail extends InspectionListItem {
  formVersion: string;
  location: string;
  coordinates: string;
  autoFill: boolean;
}

// Confirmed response shape — description/location/coordinates/dates and
// formVersion are omitted when unset.
interface ApiInspectionDetail extends ApiInspection {
  formVersion?: string;
  location?: string | null;
  coordinates?: string | null;
  autoFill: boolean;
}

const toInspectionDetail = (i: ApiInspectionDetail): InspectionDetail => ({
  ...toInspection(i),
  formVersion: i.formVersion ?? '',
  location: i.location ?? '',
  coordinates: i.coordinates ?? '',
  autoFill: i.autoFill,
});

/** POST /inspections/scope-users body — who is auto-assigned by this scope. */
export interface ScopeUsersArgs {
  project: string;
  companyLevel: string;
  companyNodes: string[];
}

/** The four lifecycle verbs behind POST /inspections/bulk-action. */
export enum InspectionBulkActionType {
  DUPLICATE = 'duplicate',
  SUSPEND = 'suspend',
  RESUME = 'resume',
  ARCHIVE = 'archive',
}

/** POST /inspections/bulk-delete body — one call for any number of rows, so a
 *  single-row delete and a whole-selection delete are the same request. */
export interface DeleteInspectionsPayload {
  inspectionIds: string[];
}

/** POST /inspections/bulk-action body. The endpoint always takes an id ARRAY,
 *  but only ARCHIVE is a multi-select action in the UI — duplicate, suspend and
 *  resume are offered on a single inspection and send a one-element array. */
export interface InspectionBulkActionArgs {
  inspectionIds: string[];
  action: InspectionBulkActionType;
}

export const inspectionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getInspections: build.query<InspectionsPage, InspectionsQueryArgs>({
      query: (body) => ({ ...ROUTES.inspections.list, data: body }),
      transformResponse: (res: ApiInspectionsPage) => {
        const items = Array.isArray(res) ? res : (res?.items ?? []);
        const total = Array.isArray(res)
          ? res.length
          : (res?.totalRecords ?? res?.total ?? items.length);
        return { items: items.map(toInspection), total };
      },
      providesTags: [{ type: 'Inspections', id: 'LIST' }],
    }),
    // Assigned-to filter source — users with access to any inspection.
    getAccessibleInspectionUsers: build.query<InspectionUserOption[], void>({
      query: () => ({ ...ROUTES.inspections.accessibleUsers }),
    }),
    createInspection: build.mutation<{ inspectionId?: string }, CreateInspectionPayload>({
      query: (body) => ({ ...ROUTES.inspections.create, data: body }),
      invalidatesTags: [{ type: 'Inspections', id: 'LIST' }],
    }),
    saveDraftInspection: build.mutation<{ inspectionId?: string }, SaveDraftInspectionPayload>({
      query: (body) => ({ ...ROUTES.inspections.saveDraft, data: body }),
      // Updating an existing draft (inspectionId set) also refreshes its detail.
      invalidatesTags: (_res, _err, { inspectionId }) => [
        { type: 'Inspections', id: 'LIST' },
        ...(inspectionId ? [{ type: 'Inspections' as const, id: `DETAIL_${inspectionId}` }] : []),
      ],
    }),
    // Single-inspection payload for the edit drawer (fields the list rows lack).
    getInspectionDetail: build.query<InspectionDetail, string>({
      query: (id) => ({ ...ROUTES.inspections.detail(id) }),
      transformResponse: toInspectionDetail,
      providesTags: (_res, _err, id) => [{ type: 'Inspections', id: `DETAIL_${id}` }],
    }),
    updateInspection: build.mutation<
      { inspectionId?: string },
      { id: string } & UpdateInspectionPayload
    >({
      query: ({ id, ...body }) => ({ ...ROUTES.inspections.update(id), data: body }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Inspections', id: 'LIST' },
        { type: 'Inspections', id: `DETAIL_${id}` },
      ],
    }),
    // Auto-assigned users for a scope — fetched on demand ("View assignees").
    // Same company-user shape as createdBy; nameless entries are dropped.
    getInspectionScopeUsers: build.query<InspectionPerson[], ScopeUsersArgs>({
      query: (body) => ({ ...ROUTES.inspections.scopeUsers, data: body }),
      transformResponse: (res: ApiInspectionUser[]) =>
        res.map(toPerson).filter((p): p is InspectionPerson => p !== null),
    }),
    // LIST only — unlike bulkActionInspections there is no DETAIL_<id> left to
    // refresh, because the rows no longer exist.
    deleteInspections: build.mutation<unknown, DeleteInspectionsPayload>({
      query: (body) => ({ ...ROUTES.inspections.bulkDelete, data: body }),
      invalidatesTags: [{ type: 'Inspections', id: 'LIST' }],
    }),
    // Duplicate / suspend / resume / archive — ONE endpoint over an id array.
    // Every verb changes what the visible tab should show (a duplicate adds a
    // row, suspend/resume rewrite `state`, archive moves the row to the other
    // tab), so the LIST tag always goes; the touched details go too because the
    // edit drawer reads `state` from them.
    bulkActionInspections: build.mutation<unknown, InspectionBulkActionArgs>({
      query: (body) => ({ ...ROUTES.inspections.bulkAction, data: body }),
      invalidatesTags: (_res, _err, { inspectionIds }) => [
        { type: 'Inspections', id: 'LIST' },
        ...inspectionIds.map((id) => ({ type: 'Inspections' as const, id: `DETAIL_${id}` })),
      ],
    }),
  }),
});

export const {
  useGetInspectionsQuery,
  useGetAccessibleInspectionUsersQuery,
  useGetInspectionDetailQuery,
  useLazyGetInspectionScopeUsersQuery,
  useCreateInspectionMutation,
  useSaveDraftInspectionMutation,
  useUpdateInspectionMutation,
  useDeleteInspectionsMutation,
  useBulkActionInspectionsMutation,
} = inspectionApi;
